import { useEffect, useState } from 'react';
import { listDids } from '../api_scripts/didsClient';

/**
 * Loads the env-allowed From-DID list from /dids and exposes:
 *   dids       — array of { did, primary } from the backend
 *   primary    — the primary DID's E.164 string (default Select value)
 *   loading    — true while the first request is in flight
 *   error      — Error if the request failed; falsy otherwise
 *
 * Hard-shared cache lives in `didsClient.js`, so multiple components mounting
 * within the same SPA session share the same in-flight request.
 *
 * On a backend that hasn't been deployed with the dids Lambda yet (e.g., a
 * test SPA temporarily pointing at an older API), the request will 404; the
 * hook reports `error` and consumers fall back to `primary = ''` so the
 * Select renders a clear "DID not configured" placeholder rather than
 * crashing the importer page.
 */
export function useDids() {
  const [state, setState] = useState({
    dids: [],
    primary: '',
    loading: true,
    error: null
  });

  useEffect(() => {
    let cancelled = false;
    listDids()
      .then((dids) => {
        if (cancelled) return;
        const primaryEntry = dids.find((d) => d.primary) ?? dids[0];
        setState({
          dids,
          primary: primaryEntry?.did ?? '',
          loading: false,
          error: null
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ dids: [], primary: '', loading: false, error: err });
      });
    return () => { cancelled = true; };
  }, []);

  return state;
}
